import { mapBasePageProperties } from './basePagePropertiesMapper';
import { getVacancies } from '3-foundation/Jobs/repository';
import { formatDate } from '3-foundation/Localization/formatDate';
import logger from '3-foundation/Logging/logger';
import { isPublicUnsolicitedApplication } from '3-foundation/Jobs/isPublicUnsolicitedApplication';

// A failing Mindkey call should render the page without jobs instead of failing the whole page
const getVacanciesOrEmpty = async (customerId?: string | null): Promise<Mindkey.VacancySimple[]> => {
	if (!customerId) {
		logger.error('Mindkey customer ID is not configured for this site');
		return [];
	}
	try {
		return await getVacancies({ customerId });
	} catch (error) {
		logger.error(`Could not get vacancies from Mindkey for customer ${customerId} \nCause: ${error}`);
		return [];
	}
};

interface Props extends Umbraco.BasePage {
	heading: string;
	titleColumnHeader: string;
	locationColumnHeader: string;
	deadlineColumnHeader: string;
	loadMoreButtonText: string;
	noResultsText: string;
	fieldsOfWorkFilterHeader: string;
	locationFilterHeader: string;
	showingCountText: string;
	applyForPositionButtonText: string;
}

export const mapJobListPageProperties = async (
	properties: Props,
	pageId: string,
	siteId: string,
	locale: string,
	site?: Site.Site,
): Promise<Content.JobListPageProperties> => {
	try {
		const vacanciesFromMindkey = await getVacanciesOrEmpty(site?.mindkeyCustomerId);

		const jobs: Content.JobVacancy[] = vacanciesFromMindkey
			// Only list public vacancies. The unsolicited application has its own page
			.filter((vacancy) => vacancy.externalAccess !== false && !vacancy.unsolicited)
			.map((vacancy) => ({
				// Mindkey ids are free text per customer (e.g. "26-665 " or "Maj26-15")
				id: vacancy.id.trim(),
				description: vacancy.description,
				endDate: vacancy.endDate ? formatDate({ dateInput: vacancy.endDate, locale }) : '',
				fieldsOfWork: Array.from(new Map((vacancy.fieldsOfWork ?? []).map(({ id, description }) => [id, { id, description }])).values()),
				locations: Array.from(new Map((vacancy.locations ?? []).map(({ id, description }) => [id, { id, description }])).values()),
			}));

		const uniqueFieldsOfWorkOptions = Array.from(
			new Map(jobs.flatMap((job) => job.fieldsOfWork).map((fieldOfWork) => [fieldOfWork.id, fieldOfWork])).values(),
		).sort((fieldOfWorkA, fieldOfWorkB) => fieldOfWorkA.description.localeCompare(fieldOfWorkB.description));

		const uniqueLocationOptions = Array.from(
			new Map(jobs.flatMap((job) => job.locations).map((location) => [location.id, location])).values(),
		).sort((locationA, locationB) => locationA.description.localeCompare(locationB.description));

		return {
			...(await mapBasePageProperties(properties, siteId, locale)),
			heading: properties.heading ?? '',
			jobs,
			// Not every Mindkey customer has an unsolicited application set up
			hasUnsolicitedApplication: vacanciesFromMindkey.some(isPublicUnsolicitedApplication),
			fieldsOfWorkOptions: uniqueFieldsOfWorkOptions,
			locationOptions: uniqueLocationOptions,
			texts: {
				titleColumnHeader: properties.titleColumnHeader,
				locationColumnHeader: properties.locationColumnHeader,
				deadlineColumnHeader: properties.deadlineColumnHeader,
				loadMoreButtonText: properties.loadMoreButtonText,
				noResultsText: properties.noResultsText,
				fieldsOfWorkFilterHeader: properties.fieldsOfWorkFilterHeader,
				locationFilterHeader: properties.locationFilterHeader,
				showingCountText: properties.showingCountText,
				applyForPositionButtonText: properties.applyForPositionButtonText,
			},
		};
	} catch (error) {
		throw new Error(`Could not map job list page properties\nCause: ${error}`, { cause: error });
	}
};
